import styled from '@emotion/styled';
import { ObjectId } from '@racemap/sdk/schema/base';
import {
  type MessageTemplate,
  MessageTemplateArgumentVariant,
} from '@racemap/sdk/schema/messageTemplates';
import { TRACKER_MESSAGE_TRANSPORT, type Tracker, USER_ROLE } from '@racemap/sdk/schema/trackers';
import { type TRACKER_TYPES, TrackerActivationType } from '@racemap/utilities/consts/trackers';
import { isDefined } from '@racemap/utilities/functions/validation';
import type { User_Legacy } from '@racemap/utilities/types/users';
import { Button, Form, Select } from 'antd';
import Fuse from 'fuse.js';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useCurrentUser } from '../../../lib/customHooks';
import { useStore } from '../../../store/reducers';
import type {
  ManagedMessageTemplate,
  ManagedTracker,
  ManagedTrackers,
} from '../../../store/trackers/trackers_reducers';
import { TrackerUpdateCount } from '../../BasicComponents/TrackerUpdateCount';
import { toastError, toastSuccess } from '../../utils/Utils';
import { ChooseTransportSelect } from './ChooseTransportSelect';
import { CloseAfterDatePicker } from './CloseAfterDatePicker';
import { PlannedAtDatePicker } from './PlannedAtDatePicker';
import { SMSCommandReport, isAnSMSReadyTracker } from './SMSCommandReport';
import { TrackerCommandForm } from './TrackerCommandForm';

type Props = {
  trackerIds: Array<ObjectId>;
  templateId?: ObjectId | null;
  onCommandsSent?: () => void;
};

export interface Parameter {
  name: string;
  argumentId: ObjectId;
  value: string;
}
export interface FormValues {
  plannedAt: DateTime;
  closeAfter: DateTime;
  instantSending: boolean;
  transport: TRACKER_MESSAGE_TRANSPORT;
  templateId: string | null;
  forceNow: boolean;
  parameters: Array<Parameter>;
}

export function AddTrackerCommando({ trackerIds, onCommandsSent = () => {} }: Props) {
  const [form] = Form.useForm<FormValues>();
  const user = useCurrentUser();
  const trackers = useStore((s) => s.trackers.items);
  const messageTemplates = useStore((s) => s.trackers.messageTemplates);
  const { addMessageToTracker } = useStore((s) => s.trackers.actions);
  const choosedTemplateId = Form.useWatch('templateId', form);
  const transport = Form.useWatch('transport', form);
  const choosedTemplate = messageTemplates.get(choosedTemplateId ?? '') || null;

  const trackerTypeRoleMapping = useMemo(
    () =>
      getTrackerTypeRoleMapping(trackers, trackerIds, user != null ? new ObjectId(user.id) : null),
    [trackers, trackerIds, user],
  );
  const trackerTypes = Array.from(trackerTypeRoleMapping.keys());
  const trackersToUse = trackerIds.map((t) => trackers.get(t.toString()));
  const activeTrackers = trackersToUse.filter(isDefined).filter(isActivTracker);
  const trackersThatCanReceiveSMS = activeTrackers.filter(isAnSMSReadyTracker);
  const targetTrackers =
    transport === TRACKER_MESSAGE_TRANSPORT.SMS ? trackersThatCanReceiveSMS : activeTrackers;

  const filteredTrackers = useMemo(
    () =>
      targetTrackers.filter((t) =>
        trackerTypesHasTemplate(t, choosedTemplate, trackerTypeRoleMapping, user),
      ),
    [targetTrackers, choosedTemplate, trackerTypeRoleMapping, user],
  );
  const [isSending, setIsSending] = useState(false);

  const handleTemplateChange = (templateId: string | null) => {
    if (templateId == null) return;
    const template = messageTemplates.get(templateId) || null;
    form.setFieldValue('parameters', getDefaultParameters(template, trackerTypes));
  };

  const sendCommandToTrackers = async (
    trackers: Array<Immutable<ManagedTracker>>,
  ): Promise<void> => {
    if (trackers?.length === 0) return;
    const trackerIds = trackers.map((t) => t.id);

    const { templateId, plannedAt, closeAfter, transport, forceNow, parameters } =
      form.getFieldsValue();
    if (templateId == null) return;

    await addMessageToTracker({
      trackerIds,
      templateId: new ObjectId(templateId),
      plannedAt: forceNow ? new Date() : plannedAt.toJSDate(),
      closeAfter: closeAfter.toJSDate(),
      transport,
      parameters: getParametersRecord(parameters),
    });
  };

  const handleCommandSend = async () => {
    if (isSending) return;

    try {
      await form.validateFields();
      setIsSending(true);
      await sendCommandToTrackers(filteredTrackers);

      toastSuccess('Command(s) sent to tracker(s)');
      setIsSending(false);
      onCommandsSent();
    } catch (e) {
      if (typeof e === 'object' && e != null && 'values' in e) {
        console.error('Form validation error:', e);
        toastError('Please fill out all required fields');
      } else {
        toastError('Error sending command(s) to tracker(s)');
        console.error('Error sending command(s) to tracker(s):', e);
      }
      setIsSending(false);
    }
  };

  const commandList = buildCommandList(
    trackerTypeRoleMapping,
    Array.from(messageTemplates.values()),
    user?.admin,
  );

  const fuseChildren = new Fuse(commandList, {
    keys: ['props.children'],
    threshold: 0.3,
  });

  return (
    <Form
      form={form}
      requiredMark={false}
      labelCol={{ flex: '150px' }}
      wrapperCol={{ flex: 1 }}
      labelAlign="left"
      initialValues={{
        plannedAt: DateTime.now(),
        closeAfter: DateTime.now().plus({ minutes: 5 }),
        forceNow: true,
        transport: TRACKER_MESSAGE_TRANSPORT.TCP,
        templateId: null,
        parameters: [],
      }}
    >
      <PlannedAtDatePicker />
      <CloseAfterDatePicker />
      <ChooseTransportSelect />
      <Form.Item
        label="Command"
        name="templateId"
        rules={[
          {
            required: true,
            message: 'Please select a command',
          },
        ]}
      >
        <Select
          allowClear
          optionFilterProp="label"
          placeholder="Select a command"
          onChange={handleTemplateChange}
          showSearch
          filterOption={(input, option) => {
            const resultChildren = fuseChildren.search(input);

            return (
              resultChildren.some(
                (command) => command.item.props.children === option?.props.children,
              ) || option?.props.value.indexOf(input) >= 0
            );
          }}
        >
          {commandList}
        </Select>
      </Form.Item>
      {choosedTemplate != null && (
        <TrackerCommandForm
          template={choosedTemplate}
          form={form}
          relevantTrackerTypes={trackerTypes}
        />
      )}
      {choosedTemplate != null && transport === TRACKER_MESSAGE_TRANSPORT.SMS && (
        <SMSCommandReport trackers={activeTrackers} />
      )}

      <ButtonContainer>
        <Button type="primary" htmlType="submit" loading={isSending} onClick={handleCommandSend}>
          Apply command
        </Button>
        <TrackerUpdateCount
          targetTrackers={filteredTrackers}
          total={trackerIds.length}
          transport={transport}
        />
      </ButtonContainer>
    </Form>
  );
}

const getTrackerTypeRoleMapping = (
  trackers: Immutable<ManagedTrackers>,
  trackerIds: Array<ObjectId>,
  userId: ObjectId | null,
): Map<TRACKER_TYPES, Set<USER_ROLE>> => {
  if (userId == null) return new Map();
  const mapping: Map<TRACKER_TYPES, Set<USER_ROLE>> = new Map();

  for (const trackerId of trackerIds) {
    if (trackers.has(trackerId.toHexString()) != null) {
      const tracker = trackers.get(trackerId.toHexString());
      if (tracker == null) continue;

      if (!mapping.has(tracker.trackerType)) {
        mapping.set(tracker.trackerType, new Set());
      }
      const roles = getTrackerRoles(tracker, userId);
      const roleSet = mapping.get(tracker.trackerType);
      roles.forEach((r) => roleSet?.add(r));
    }
  }

  return mapping;
};

const getTrackerRoles = (tracker: Immutable<Tracker>, userId: ObjectId): Set<USER_ROLE> => {
  const roles = new Set<USER_ROLE>();

  if (tracker.owner.equals(userId)) {
    roles.add(USER_ROLE.OWNER);
  }
  if (tracker.editors.some((e) => e.equals(userId))) {
    roles.add(USER_ROLE.EDITOR);
  }
  if (
    tracker.borrowers.some(
      (b) =>
        b.id.equals(userId) &&
        b.startTime.getTime() < Date.now() &&
        b.endTime.getTime() > Date.now(),
    )
  ) {
    roles.add(USER_ROLE.BORROWER);
  }

  return roles;
};

const getParametersRecord = (parameters: Array<Parameter> | undefined): Record<string, string> => {
  if (parameters == null) return {};

  return Object.fromEntries(parameters.map((p) => [p.argumentId.toHexString(), p.value]));
};

const getDefaultParameters = (
  template: Immutable<ManagedMessageTemplate> | null,
  relevantTrackerTypes: Array<TRACKER_TYPES>,
): Array<Parameter> => {
  const defaultParameters: Array<Parameter> = [];
  if (template == null) return defaultParameters;

  for (const argument of template.arguments) {
    if (argument.variant !== MessageTemplateArgumentVariant.PARAMETER) continue;
    if (argument.trackerTypes?.every((t) => !relevantTrackerTypes.includes(t))) continue;

    defaultParameters.push({
      argumentId: argument.id,
      name: argument.name,
      value: argument.default != null ? argument.default : '',
    });
  }

  return defaultParameters;
};

const buildCommandList = (
  trackerTypesRoleMapping: Map<TRACKER_TYPES, Set<USER_ROLE>>,
  messageTemplates: Immutable<Array<MessageTemplate>>,
  isAdmin?: boolean,
) => {
  const optionList = [];

  for (const template of messageTemplates) {
    // find all roles that can use this template
    const roles = template.trackerTypes.reduce((acc, trackerType) => {
      Array.from(trackerTypesRoleMapping.get(trackerType) || [])
        .filter((r) => template.allowedTrackerUserRoles.includes(r))
        .forEach((role) => acc.add(role));
      return acc;
    }, new Set<USER_ROLE>());

    if (isAdmin || roles.size > 0) {
      optionList.push(
        <Select.Option key={template.id.toHexString()} value={template.id.toHexString()}>
          {template.name}
        </Select.Option>,
      );
    }
  }
  return optionList;
};

const isActivTracker = (tracker: Immutable<ManagedTracker>): boolean => {
  return (
    tracker.activation.type !== TrackerActivationType.ARCHIVED &&
    tracker.activation.type !== TrackerActivationType.NONE
  );
};

const trackerTypesHasTemplate = (
  tracker: Immutable<ManagedTracker>,
  template: Immutable<MessageTemplate> | null,
  trackerTypeRoleMapping: Map<TRACKER_TYPES, Set<USER_ROLE>>,
  user: Immutable<User_Legacy> | null,
): boolean => {
  if (template == null || user == null) return false;
  if (!template.trackerTypes.includes(tracker.trackerType)) return false;
  if (user.admin) return true;

  const roles = trackerTypeRoleMapping.get(tracker.trackerType);
  if (roles == null || roles.size === 0) return false;

  const userRole = getTrackerRoles(tracker, new ObjectId(user.id));
  return Array.from(roles).some((r) => userRole.has(r));
};

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 15px;
  flex-direction: row-reverse
`;
